import { keyBy, omit } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Entity } from "../../utils/constants";
import { Placement } from "../../utils/types";
import usePrevious from "../../utils/usePrevious";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";

interface PlacementsMap {
  [linkedId: string]: Placement;
}

type ContextValue = {
  placements: PlacementsMap;
  fetchPlacements: () => Promise<PlacementsMap>;
  addPlacement: (
    values: PlacementValues
  ) => Promise<void | ApiResponse<Placement>>;
  updatePlacement: (
    placementId: string,
    values: PlacementValues
  ) => Promise<void | ApiResponse<Placement>>;
  removePlacement: (placement: Placement) => Promise<ApiResponse<[]>>;
};

const PlacementsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const PlacementsProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId } = useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const [placements, setPlacements] = useState<PlacementsMap>({});

  const fetchPlacements = useCallback(async () => {
    if (!currentWorkspaceId) return {};

    addLoader(LoadingKey.workspacePlacements);

    const res = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/placements`
    );

    const apiResponse: ApiResponse<Placement[]> = await res.json();

    let newPlacements = {};

    if (apiResponse.status) {
      newPlacements = keyBy(apiResponse.data, "placementable_id");

      setPlacements(newPlacements);
    }

    removeLoader(LoadingKey.workspacePlacements);

    return newPlacements;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setPlacements({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    fetchPlacements();
  }, [fetchPlacements]);

  const addPlacement = useCallback(
    async (values: PlacementValues) => {
      if (!currentWorkspaceId) return Promise.reject();

      addLoader(LoadingKey.savingPlacement);

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/placements`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Placement> = await response.json();

      await fetchPlacements();

      removeLoader(LoadingKey.savingPlacement);

      return apiResponse;
    },
    [addLoader, currentWorkspaceId, fetchPlacements, removeLoader]
  );

  const updatePlacement = useCallback(
    async (placementId: string, values: PlacementValues) => {
      if (!currentWorkspaceId) return Promise.reject();

      addLoader(LoadingKey.savingPlacement);

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/placements/${placementId}`,
        {
          method: "PUT",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Placement> = await response.json();

      await fetchPlacements();

      removeLoader(LoadingKey.savingPlacement);

      return apiResponse;
    },
    [addLoader, currentWorkspaceId, fetchPlacements, removeLoader]
  );

  const removePlacement = useCallback(
    async (placement: Placement) => {
      setPlacements((prevPlacements) =>
        omit(prevPlacements, placement.placementable_id)
      );

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/placements/${placement.uuid}`,
        { method: "DELETE" }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      await fetchPlacements();

      return apiResponse;
    },
    [currentWorkspaceId, fetchPlacements]
  );

  const value = useMemo(
    () => ({
      placements,
      fetchPlacements,
      addPlacement,
      updatePlacement,
      removePlacement
    }),
    [
      placements,
      fetchPlacements,
      addPlacement,
      updatePlacement,
      removePlacement
    ]
  );

  return <PlacementsContext.Provider value={value} {...props} />;
};

export const usePlacements = (): ContextValue => {
  const context = useContext(PlacementsContext);
  if (context === undefined) {
    throw new Error("usePlacements must be used within an PlacementsProvider");
  }
  return context;
};

//
// Utils
//

export interface PlacementValues {
  placementable_id: string;
  placementable_type: Entity.hotspot | Entity.location;
  lat: number;
  lng: number;
}
